import {
  Col,
  Form,
  Modal,
  Row,
  Select,
  Table,
  Typography,
  Tag as ANTDTag,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { searchInvoiceForTaxInvoice } from '../../api/constlink';
import { ColumnsType } from 'antd/lib/table';
import { priceToString } from '../../util/utils';
import { styledTable } from '../styled/mixin';
import {
  Invoice,
  invoiceStatusKorean,
  TaxInvoice,
} from '../../modules/invoices';
import TextArea from 'antd/lib/input/TextArea';
import Title from 'antd/lib/typography/Title';

const StyledDiv = styled.div`
  ${styledTable}
`;
const { Text } = Typography;
const { Option } = Select;

const columns: ColumnsType<Invoice> = [
  {
    title: '공사명',
    dataIndex: ['project', 'name'],
    ellipsis: true,
    width: '20%',
  },
  {
    title: '거래처명',
    dataIndex: ['vendor', 'application', 'general', 'name'],
    ellipsis: true,
    width: '15%',
  },
  {
    title: '용도',
    dataIndex: 'category',
    ellipsis: true,
    width: '7%',
  },
  {
    title: '청구일',
    dataIndex: 'billingDate',
    width: '12%',
    sorter: (a: Invoice, b: Invoice) =>
      a.billingDate.localeCompare(b.billingDate),
    render: (text: any, record: any, index: any) =>
      record.usedDate === null
        ? ''
        : dayjs(record.billingDate).format('YYYY-MM-DD'),
  },
  {
    title: '상태',
    dataIndex: 'status',
    width: '7%',
    render: (text: any, record: Invoice) => invoiceStatusKorean[record.status],
  },
  {
    title: '공급가액(원)',
    width: '12%',
    align: 'right',
    render: (value: any, record: any, index: any) =>
      priceToString(record.price - record.vat),
  },
  {
    title: '부가세(원)',
    dataIndex: 'vat',
    width: '12%',
    align: 'right',
    render: (value: any, record: any, index: any) => priceToString(value),
  },
  {
    title: '합계금액(원)',
    dataIndex: 'price',
    sorter: (a: Invoice, b: Invoice) => a.price - b.price,
    width: '12%',
    align: 'right',
    render: (value: any, record: any, index: any) => priceToString(value),
  },
  {
    title: '증빙확인',
    width: '10%',
    align: 'center',
    render: (value: any, record: any, index: any) => {
      let result = '';
      let color = '';
      const _totalConnectedTaxInvoicePrice = record.taxInvoices.reduce(
        (acc: number, cur: any) => acc + cur.taxAmt + cur.supplyValue,
        0,
      );
      const _totalInvoicePrice = record.price;
      if (record.vat === 0) {
        return 'N/A';
      }
      if (_totalConnectedTaxInvoicePrice === 0) {
        result = '아니오';
        color = 'red';
      } else if (_totalInvoicePrice === _totalConnectedTaxInvoicePrice) {
        result = '예';
        color = 'blue';
      } else if (_totalInvoicePrice > _totalConnectedTaxInvoicePrice) {
        result = '진행중';
        color = 'green';
      } else {
        result = '분할';
        color = 'olive';
      }
      return <ANTDTag color={color}>{result}</ANTDTag>;
    },
  },
];

interface MappingInvoiceModalProps {
  selectedTaxInvoice: TaxInvoice | null;
  enterpriseId: string;
  onCreate: (idList: number[], description: string) => void;
  onCancel: () => void;
}

function MappingInvoiceModal({
  selectedTaxInvoice,
  enterpriseId,
  onCreate,
  onCancel,
}: MappingInvoiceModalProps) {
  const [form] = Form.useForm();
  const [selectedRowKeysLocal, setSelectedRowKeysLocal] = useState<React.Key[]>(
    [],
  );
  const [selectedInvoices, setSelectedInvoices] = useState<any>([]);
  const [duration, setDuration] = useState('1');
  const [invoices, setInvoices] = useState<any[]>([]);
  const [totalInvoicePrice, setTotalInvoicePrice] = useState(0);

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedInvoices([...selectedRows]);
      setSelectedRowKeysLocal(selectedRowKeys);
    },
    // getCheckboxProps: (record: any) => ({
    //   disabled: record.disabled,
    //   // selected: selectedRowKeysLocal.includes(record.id),
    // }),
    selectedRowKeys: selectedRowKeysLocal,
  };

  useEffect(() => {
    if (selectedTaxInvoice === null) return;
    (async () => {
      const startDate = dayjs()
        .subtract(+duration * 30, 'day')
        .format('YYYY-MM-DD');
      const endDate = dayjs().format('YYYY-MM-DD');

      const result: any = await searchInvoiceForTaxInvoice({
        enterpriseId,
        filterData: {
          startDate,
          endDate,
          corporateNumber: selectedTaxInvoice.supplierRegNumber,
          selectedTaxInvoiceId: selectedTaxInvoice.id,
        },
      });
      if (result.success) {
        const totalConnectedInvoicePrice = selectedTaxInvoice.invoices.reduce(
          (acc: number, cur: any) => acc + cur.price,
          0,
        );
        const _invoices: Invoice[] = [];
        for (const searchedInvoice of result.invoices) {
          if (selectedTaxInvoice.invoices.length === 0) {
            if (searchedInvoice.taxInvoices.length === 0) {
              _invoices.push(searchedInvoice);
            }
          } else {
            const selectedTaxInvoicePrice =
              selectedTaxInvoice.taxAmt + selectedTaxInvoice.supplyValue;
            if (selectedTaxInvoicePrice <= totalConnectedInvoicePrice) {
              /**
               * 선택된 매입증빙과 연결된 청구서의 합계금액이 선택된 매입증빙 금액보다 크거나 같을 때
               *  - 선택된 매입증빙과 연결된 청구서
               */
              for (const connectedInvoice of selectedTaxInvoice.invoices) {
                if (connectedInvoice.id === searchedInvoice.id) {
                  _invoices.push(searchedInvoice);
                  break;
                }
              }
            } else if (selectedTaxInvoicePrice > totalConnectedInvoicePrice) {
              /**
               * 선택된 매입증빙과 연결된 청구서의 합계금액이 선택된 매입증빙 금액보다 작을 때
               *  - 선택된 매입증빙과 연결된 청구서
               *  - 다른 지출증빙과 연결 안된 청구서
               */
              for (const connectedInvoice of selectedTaxInvoice.invoices) {
                if (connectedInvoice.id === searchedInvoice.id) {
                  _invoices.push(searchedInvoice);
                  break;
                } else {
                  if (searchedInvoice.taxInvoices.length === 0) {
                    _invoices.push(searchedInvoice);
                    break;
                  }
                }
              }
            }
          }
        }

        setInvoices(_invoices);

        if (selectedTaxInvoice.invoices.length > 0) {
          setSelectedInvoices(selectedTaxInvoice.invoices);
          const idList = selectedTaxInvoice.invoices.map(
            (invoice) => invoice.id,
          );
          setSelectedRowKeysLocal(idList);
        } else {
          setSelectedInvoices([]);
          setSelectedRowKeysLocal([]);
        }
      } else {
        console.error(result.message);
      }
    })();
  }, [enterpriseId, selectedTaxInvoice, duration]);

  useEffect(() => {
    if (selectedTaxInvoice === null) {
      return;
    }

    let _totalInvoicePrice = 0;
    for (const invoice of selectedInvoices) {
      _totalInvoicePrice += invoice.price;
    }
    setTotalInvoicePrice(_totalInvoicePrice);
  }, [selectedInvoices, selectedTaxInvoice]);

  return (
    <StyledDiv>
      <Modal
        open={selectedTaxInvoice !== null}
        title="매입증빙과 청구서 연결"
        okText="확인"
        cancelText="취소"
        width={1100}
        onCancel={onCancel}
        onOk={() => {
          form.validateFields().then((values) => {
            const _enterpriseIdList: number[] = [];
            for (const selectedInvoice of selectedInvoices) {
              _enterpriseIdList.push(selectedInvoice.id);
            }
            onCreate(_enterpriseIdList, values.description);
          });
        }}
      >
        {selectedTaxInvoice && (
          <>
            <Row>
              <Col span={24}>
                <Title level={5}>매입증빙</Title>
              </Col>
            </Row>
            <Row style={{ height: '40px' }}>
              <Col span={24}>
                <Text>
                  {selectedTaxInvoice.reportingDate} |{' '}
                  {selectedTaxInvoice.type === '0'
                    ? '세금계산서'
                    : '현금영수증'}{' '}
                  | {selectedTaxInvoice.supplierCompanyName} |{' '}
                  {priceToString(
                    selectedTaxInvoice.taxAmt + selectedTaxInvoice.supplyValue,
                  )}
                  원
                </Text>
              </Col>
            </Row>
            <Form
              form={form}
              size="small"
              initialValues={{
                duration,
                description: selectedTaxInvoice.description,
              }}
            >
              <Row justify={'space-between'} style={{ height: '20px' }}>
                <Col span={6}>
                  <Title level={5}>청구서</Title>
                </Col>
                <Col span={4}>
                  <Form.Item name="duration">
                    <Select
                      style={{ width: '150px' }}
                      onSelect={async (key: any, option: any) => {
                        setDuration(key);
                      }}
                    >
                      <Option value="1">최근 1개월</Option>
                      <Option value="2">최근 2개월</Option>
                      <Option value="3">최근 3개월</Option>
                      <Option value="6">최근 6개월</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Table
                  rowKey="id"
                  size="small"
                  columns={columns}
                  style={{ marginTop: '10px' }}
                  rowSelection={{ ...rowSelection, checkStrictly: false }}
                  pagination={false}
                  dataSource={invoices}
                  summary={() => {
                    return (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}></Table.Summary.Cell>
                          <Table.Summary.Cell index={1}></Table.Summary.Cell>
                          <Table.Summary.Cell index={2}></Table.Summary.Cell>
                          <Table.Summary.Cell index={3}></Table.Summary.Cell>
                          <Table.Summary.Cell index={4}></Table.Summary.Cell>
                          <Table.Summary.Cell index={5}></Table.Summary.Cell>
                          <Table.Summary.Cell index={6}></Table.Summary.Cell>
                          <Table.Summary.Cell
                            index={7}
                            align="right"
                            rowSpan={3}
                          >
                            합계금액:
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={8} align="right">
                            {priceToString(totalInvoicePrice)}원
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={9}></Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    );
                  }}
                />
              </Row>
              <Row style={{ marginTop: '20px' }}>
                <Col span={20}>
                  <Form.Item
                    name="description"
                    label="비고"
                    style={{ width: '600px' }}
                  >
                    <TextArea rows={1} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Modal>
    </StyledDiv>
  );
}

export default MappingInvoiceModal;
